import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Loader2, Pencil, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { http, errorText } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CATEGORIES, formatDate } from "../lib/posts";
import { Logo } from "../components/site/Logo";
import { useTitle } from "../lib/useTitle";

const COVERS = ["/images/lab.jpg", "/images/w-cows.jpg", "/images/cows.jpg", "/images/w-fishfarm.jpg", "/images/shrimp.jpg", "/images/w-poultry.jpg", "/images/senzia.jpg", "/images/lab.jpg", "/images/w-pasture.jpg"];
const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ title: "", category: CATEGORIES[0], summary: "", body: "", cover: COVERS[0], published_at: today() });

const Label = ({ children }) => <span className="block text-[11px] font-bold uppercase tracking-widest text-[var(--muted)] mb-1">{children}</span>;

const Editor = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { title: f.title, category: f.category, summary: f.summary, body: f.body, cover: f.cover, published_at: f.published_at };
    try {
      const { data } = f.id ? await http.put(`/admin/posts/${f.id}`, payload) : await http.post("/admin/posts", payload);
      toast.success("Article saved");
      onSaved(data);
    } catch (ex) { toast.error(errorText(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const box = "w-full rounded-md border border-[var(--line)] bg-white px-3 py-2.5 text-[15px] outline-none focus:border-[var(--forest)]";
  return (
    <form onSubmit={save} data-testid="post-editor" className="bg-white rounded-[10px] border border-[var(--line)] p-5 md:p-8 space-y-5">
      <label className="block"><Label>Title *</Label><input data-testid="post-title-input" required minLength={3} className={box} value={f.title} onChange={set("title")} /></label>
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block"><Label>Category</Label>
          <select data-testid="post-category-select" className={box} value={f.category} onChange={set("category")}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        </label>
        <label className="block"><Label>Publish date</Label><input data-testid="post-date-input" type="date" required className={box} value={f.published_at} onChange={set("published_at")} /></label>
      </div>
      <label className="block"><Label>Summary *</Label><textarea data-testid="post-summary-input" required minLength={3} rows={2} className={box} value={f.summary} onChange={set("summary")} /></label>
      <label className="block"><Label>Article text * (leave a blank line between paragraphs)</Label><textarea data-testid="post-body-input" required minLength={3} rows={10} className={box} value={f.body} onChange={set("body")} /></label>
      <div>
        <Label>Cover image</Label>
        <div className="flex flex-wrap gap-2">
          {COVERS.map((c) => (
            <button type="button" key={c} onClick={() => setF({ ...f, cover: c })} className={`h-16 w-24 rounded overflow-hidden border-2 ${f.cover === c ? "border-[var(--leaf)]" : "border-transparent"}`}>
              <img src={c} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
        <input data-testid="post-cover-input" className={`${box} mt-3`} placeholder="…or paste an image link (https://…)" value={f.cover} onChange={set("cover")} />
      </div>
      <div className="flex gap-3">
        <button data-testid="post-save-button" disabled={busy} className="inline-flex items-center gap-2 bg-[var(--moss)] text-white px-6 py-3 rounded-md font-bold disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} Save article</button>
        <button type="button" data-testid="post-cancel-button" onClick={onCancel} className="px-6 py-3 rounded-md border border-[var(--line)] font-semibold">Cancel</button>
      </div>
    </form>
  );
};

export default function AdminNews() {
  useTitle("News admin | Gait Naurish Ltd.");
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    http.get("/posts", { params: { limit: 100 } }).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([]));
    return undefined;
  }, [user]);

  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const onSaved = (p) => {
    setPosts((xs) => [p, ...(xs || []).filter((x) => x.id !== p.id)].sort((a, b) => (a.published_at < b.published_at ? 1 : -1)));
    setEditing(null);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await http.delete(`/admin/posts/${id}`); setPosts((xs) => xs.filter((x) => x.id !== id)); }
    catch (e) { toast.error(errorText(e.response?.data?.detail)); }
  };

  return (
    <main data-testid="admin-news-page" className="min-h-screen bg-[var(--paper)]">
      <header className="border-b border-[var(--line)] bg-white">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 py-4 flex items-center justify-between">
          <Logo />
          <Link to="/admin" data-testid="admin-news-back" className="inline-flex items-center gap-2 text-sm font-semibold"><ArrowLeft size={14} /> Enquiries</Link>
        </div>
      </header>
      <section className="mx-auto max-w-[1100px] px-5 md:px-10 py-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <h1 className="font-display text-4xl text-[var(--forest)]">News &amp; insights</h1>
          {!editing && <button data-testid="post-new-button" onClick={() => setEditing(blank())} className="inline-flex items-center gap-2 bg-[var(--moss)] text-white px-5 py-3 rounded-md font-bold"><Plus size={16} /> New article</button>}
        </div>
        {editing ? (
          <Editor key={editing.id || "new"} initial={editing} onSaved={onSaved} onCancel={() => setEditing(null)} />
        ) : (
          <div className="space-y-3">
            {posts === null && <Loader2 className="animate-spin" />}
            {posts && posts.length === 0 && <p className="text-[var(--muted)]">No articles yet.</p>}
            {(posts || []).map((p) => (
              <div key={p.id} data-testid={`admin-post-${p.slug}`} className="bg-white rounded-[10px] border border-[var(--line)] p-4 flex items-center gap-4">
                {p.cover && <img src={p.cover} alt="" className="h-16 w-24 rounded object-cover shrink-0 hidden sm:block" />}
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg text-[var(--forest)] truncate">{p.title}</p>
                  <p className="text-xs text-[var(--muted)]">{p.category} · {formatDate(p.published_at)}</p>
                </div>
                <Link to={`/news/${p.slug}`} target="_blank" aria-label="View" className="p-2 rounded border border-[var(--line)]"><ExternalLink size={15} /></Link>
                <button data-testid={`admin-post-edit-${p.slug}`} onClick={() => setEditing(p)} aria-label="Edit" className="p-2 rounded border border-[var(--line)]"><Pencil size={15} /></button>
                <button data-testid={`admin-post-delete-${p.slug}`} onClick={() => remove(p.id)} aria-label="Delete" className="p-2 rounded border border-[var(--line)] hover:text-red-700"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
